<template>
  <div class="page page-projects">
    <el-container class="page-container">
      <el-header class="page-header" height="56px">
        <div class="title">作品库</div>
        <div>
          <el-input
            v-model="keyword"
            size="small"
            placeholder="搜索作品名称"
            prefix-icon="el-icon-search"
            class="search-input"
            clearable
          />
          <el-button
            icon="el-icon-setting"
            size="small"
            class="config-btn"
            @click="goAppConfig"
          >
            模型配置
          </el-button>
          <el-dropdown trigger="click" @command="handleCreateCommand">
            <el-button type="primary" size="small">
              新建作品 <i class="el-icon-arrow-down el-icon--right" />
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="short" icon="el-icon-document">短篇小说</el-dropdown-item>
              <el-dropdown-item command="long" icon="el-icon-notebook-2">长篇小说</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </el-header>
      <el-main class="page-main">
        <el-row :gutter="20">
          <el-col
            v-for="item in filteredProjects"
            :key="item.id"
            :xs="12"
            :sm="8"
            :md="6"
            :lg="4"
          >
            <el-card class="project-card" shadow="hover" @click.native="goWorkbench(item)">
              <el-button
                class="project-edit-btn"
                type="primary"
                size="mini"
                icon="el-icon-edit"
                @click.stop="openEdit(item)"
              >
                编辑
              </el-button>
              <div class="card-cover">
                <div
                  class="cover-inner"
                  :class="{ 'cover-placeholder': !item.cover }"
                  :style="item.cover ? { backgroundImage: 'url(' + item.cover + ')' } : {}"
                >
                  <span v-if="!item.cover" class="cover-title">
                    {{ item.title }}
                  </span>
                </div>
              </div>
              <div class="card-info">
                <div class="project-title text-ellipsis">{{ item.title }}</div>
                <div class="project-author text-ellipsis">
                  <i class="el-icon-user" />{{ item.author || '佚名' }}
                </div>
                <div class="project-intro text-multi-ellipsis">
                  {{ item.intro || '这是一部正在创作中的精彩故事。' }}
                </div>
                <div class="project-meta">
                  <span><i class="el-icon-document" />{{ item.words ? item.words.toLocaleString() : 0 }} 字</span>
                  <span>{{ item.updatedAt ? item.updatedAt.slice(0, 10) : '' }}</span>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </el-main>
    </el-container>

    <short-novel-wizard
      :visible.sync="wizardVisible"
      @finish="handleWizardFinish"
    />

    <long-novel-wizard
      :visible.sync="longWizardVisible"
      @finish="handleWizardFinish"
    />

    <el-dialog
      title="编辑作品信息"
      :visible.sync="editDialogVisible"
      width="480px"
    >
      <el-form :model="editForm" label-width="70px" size="small">
        <el-form-item label="封面">
          <image-upload
            v-model="editForm.cover"
            folder-name="cover"
            :limit="1"
            :width="90"
            :height="120"
            :file-type="['jpg','jpeg','png','webp']"
          />
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="editForm.title" placeholder="请输入作品标题" />
        </el-form-item>
        <el-form-item label="作者">
          <el-input v-model="editForm.author" placeholder="请输入作者名称" />
        </el-form-item>
        <el-form-item label="简介">
          <el-input
            type="textarea"
            :rows="3"
            v-model="editForm.intro"
            placeholder="请输入作品简介"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button type="danger" icon="el-icon-delete" :loading="editDeleting" @click="handleDelete">删 除</el-button>
        <div class="footer-right">
          <el-button @click="editDialogVisible = false">取 消</el-button>
          <el-button type="primary" :loading="editSaving" @click="submitEdit">保 存</el-button>
        </div>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ShortNovelWizard from '@/components/ShortNovelWizard.vue';
import LongNovelWizard from '@/components/LongNovelWizard.vue';
import ImageUpload from '@/components/ImageUpload/index.vue';
import { updateProject, deleteProject } from '@/api/project';

export default {
  name: 'ProjectList',
  components: { ShortNovelWizard, LongNovelWizard, ImageUpload },
  data() {
    return {
      keyword: '',
      wizardVisible: false,
      longWizardVisible: false,
      editDialogVisible: false,
      editSaving: false,
      editDeleting: false,
      editForm: {
        id: null,
        title: '',
        author: '',
        intro: '',
        cover: ''
      },
      editOriginalCover: ''   // 记录打开编辑时的原始封面，用于判断是否主动清空
    };
  },
  computed: {
    ...mapState(['projects']),
    filteredProjects() {
      if (!this.keyword) return this.projects;
      const kw = this.keyword.trim().toLowerCase();
      return this.projects.filter(p => p.title.toLowerCase().includes(kw));
    },
  },
  created() {
    this.$store.dispatch('loadProjects');
  },
  methods: {
    handleCreateCommand(command) {
      if (command === 'short') this.wizardVisible = true;
      else if (command === 'long') this.longWizardVisible = true;
    },
    goWorkbench(item) {
      this.$router.push({ name: 'Workbench', params: { id: item.id } });
    },
    handleWizardFinish() {
      this.$store.dispatch('loadProjects');
    },
    openEdit(item) {
      this.editOriginalCover = item.cover || '';
      this.editForm = {
        id: item.id,
        title: item.title,
        author: item.author || '',
        intro: item.intro || '',
        cover: item.cover || ''
      };
      this.editDialogVisible = true;
    },
    async submitEdit() {
      if (!this.editForm.title) {
        this.$message.warning('请输入作品标题');
        return;
      }
      this.editSaving = true;
      // cover 处理规则：
      //   - 有值（COS URL）：正常传递，后端更新封面
      //   - 空字符串 且 原来就没封面：传 null，后端跳过该字段
      //   - 空字符串 且 原来有封面：说明用户主动删除了封面，传空字符串，后端清空封面
      const coverValue = this.editForm.cover
        || (this.editOriginalCover ? '' : null);
      const payload = {
        title: this.editForm.title,
        author: this.editForm.author,
        intro: this.editForm.intro,
        cover: coverValue
      };
      try {
        await updateProject(this.editForm.id, payload);
        this.$store.commit('updateProject', {
          id: this.editForm.id,
          ...payload
        });
        this.$message.success('保存成功');
        this.editDialogVisible = false;
      } catch (e) {
        // 错误已由 request 拦截器统一提示
      } finally {
        this.editSaving = false;
      }
    },
    handleDelete() {
      this.$confirm(`确定要删除《${this.editForm.title}》吗？删除后不可恢复。`, '删除确认', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning',
        confirmButtonClass: 'el-button--danger'
      }).then(async () => {
        this.editDeleting = true;
        try {
          await deleteProject(this.editForm.id);
          this.$store.commit('removeProject', this.editForm.id);
          this.$message.success('作品已删除');
          this.editDialogVisible = false;
        } catch (e) {
          // 错误已由 request 拦截器统一提示
        } finally {
          this.editDeleting = false;
        }
      }).catch(() => {});
    },
    goAppConfig() {
      this.$router.push({ name: 'AppConfig' });
    }
  }
};
</script>

<style scoped>
.page-projects {
  height: 100%;
  background: #f5f7fa;
  color: #303133;
}
.page-container {
  height: 100%;
}
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  border-bottom: 1px solid #ebeef5;
  background: #ffffff;
}
.title {
  font-size: 16px;
  font-weight: 600;
}
.search-input {
  margin-right: 12px;
  width: 220px;
}
.config-btn {
  margin-right: 12px;
}
.page-main {
  padding: 16px 24px;
  background: #f5f7fa;
}
/* 去掉 el-card 默认 padding，由我们完全接管布局 */
.project-card ::v-deep .el-card__body {
  padding: 0;
}
.project-card {
  position: relative;
  margin-bottom: 20px;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;
}
.project-card:hover {
  border-color: #ff9472;
  box-shadow: 0 6px 20px rgba(255, 127, 80, 0.15);
  transform: translateY(-2px);
}

/* 封面 */
.card-cover {
  width: 100%;
}
.cover-inner {
  width: 100%;
  padding-top: 133%; /* 3:4 */
  position: relative;
  background-size: cover;
  background-position: center;
  background-color: #f2f6fc;
  overflow: hidden;
}
.cover-placeholder {
  background-image: linear-gradient(150deg, #ffb199 0%, #ff7f50 60%, #ff5f6d 100%);
}
.cover-title {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  text-align: center;
  text-shadow: 0 1px 4px rgba(0,0,0,0.3);
  line-height: 1.4;
  word-break: break-all;
}

/* 卡片信息区 */
.card-info {
  padding: 10px 12px 12px;
  border-top: 1px solid #f5f5f5;
}
.project-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 4px;
  letter-spacing: 0.2px;
  line-height: 1.4;
}
.project-author {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #b0b4bd;
  margin-bottom: 6px;
}
.project-author i {
  font-size: 11px;
}
.project-intro {
  font-size: 12px;
  color: #8a8f9c;
  line-height: 1.6;
  margin-bottom: 10px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.project-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  color: #c0c4cc;
  padding-top: 8px;
  border-top: 1px solid #f5f7fa;
}
.project-meta span {
  display: flex;
  align-items: center;
  gap: 3px;
}
.project-meta i {
  font-size: 11px;
}

/* dialog footer 布局：删除靠左，取消/保存靠右 */
.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.footer-right {
  display: flex;
  gap: 8px;
}

/* 编辑按钮 */
.project-edit-btn {
  position: absolute;
  right: 8px;
  top: 8px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 2;
  border-radius: 6px !important;
}
.project-card:hover .project-edit-btn {
  opacity: 1;
}

.text-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.text-multi-ellipsis {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

